import { useState } from 'react'
import qs from 'qs'

import {
  LoadingContainer,
  Modal,
  Input,
  LegacyPage,
  Panels,
  Bar,
  Pagination,
  Form,
  Select,
} from 'peach/components'
import { ListSubroutes, useParams } from 'peach/router'
import { CrossCompanyRolesSelect } from 'peach/scopes/session'

import { AutoFilters } from 'core/components'
import { useQueryString } from 'core/hooks'
import {
  useInternalUsersGet,
  useInternalUserCreate,
  PickRequired,
} from 'core/types'
import {
  internalUsersGet,
  InternalUsersGetVariables,
} from 'core/types/operations'

import './UserFilters'

const QUERY_DEFAULTS = {
  limit: 50,
  startingAfter: '',
  endingBefore: '',
}

const CompanyUsers = () => {
  const { companyId } = useParams<{ companyId?: string }>()

  const [page, setPage] = useState(1)

  const [query, setQuery] =
    useQueryString<
      PickRequired<
        NonNullable<InternalUsersGetVariables['query']>,
        'limit' | 'startingAfter' | 'endingBefore'
      >
    >(QUERY_DEFAULTS)

  const {
    data: users,
    pagination: { total: count, nextUrl, previousUrl } = {},
    isLoading,
    refetch,
  } = useInternalUsersGet({ companyId, query })

  const nextUser = qs.parse(nextUrl ?? '')?.startingAfter
  const previousUser = qs.parse(previousUrl ?? '')?.endingBefore

  const { mutateAsync: createUser } = useInternalUserCreate({ companyId })

  return (
    <Panels
      header={
        <Bar
          left={
            <Pagination
              limit={query.limit ?? 0}
              onNext={() => {
                setQuery({
                  ...query,
                  startingAfter: nextUser,
                  endingBefore: '',
                })
                setPage(page + 1)
              }}
              onPrevious={() => {
                setQuery({
                  ...query,
                  startingAfter: '',
                  endingBefore: page > 2 ? previousUser : '',
                })
                setPage(page - 1)
              }}
              page={page}
              total={count}
              variant='compact'
            />
          }
          right={
            <AutoFilters
              defaults={QUERY_DEFAULTS}
              onChange={(patch) => {
                setQuery({ ...patch, startingAfter: '', endingBefore: '' })
                setPage(1)
              }}
              operation={internalUsersGet}
              query={query}
            />
          }
          title='Users'
        />
      }
    >
      <LoadingContainer isLoading={isLoading}>
        <Modal
          initialValue={{
            type: 'agent',
            authType: {
              email: '',
              phone: '',
              password: '',
              samlSubjectNameId: '',
            },
            roleIds: [],
          }}
          onSubmit={async (values) => {
            // Remove empty/falsy fields from authType
            const cleanedAuthType = values.authType
              ? Object.fromEntries(
                  Object.entries(values.authType).filter(
                    ([, value]) =>
                      value !== null && value !== undefined && value !== '',
                  ),
                )
              : undefined

            // Only include authType if it has at least one field
            const cleanedValues = {
              ...values,
              authType:
                cleanedAuthType && Object.keys(cleanedAuthType).length > 0
                  ? cleanedAuthType
                  : undefined,
            }

            await createUser(cleanedValues)
            await refetch()
          }}
          title='Add User'
          trigger
        >
          <Select
            formKey='type'
            label='User Type'
            options={[
              { value: 'borrower', label: 'Borrower' },
              { value: 'agent', label: 'Agent' },
              { value: 'service', label: 'Service' },
            ]}
          />

          <Form.Provider formKey='authType' section>
            <Input formKey='email' label='Email' type='email' />
            <Input formKey='phone' label='Phone' type='tel' />
            <Input formKey='password' label='Password' type='password' />
            <Input formKey='samlSubjectNameId' label='SAML Subject Name ID' />
          </Form.Provider>

          <Input formKey='borrowerId' label='Borrower ID' />

          <CrossCompanyRolesSelect companyId={companyId} formKey='roleIds' />
        </Modal>
        <LegacyPage.Full>
          <ListSubroutes json={users} paramKey='userId' title='Users' />
        </LegacyPage.Full>
      </LoadingContainer>
    </Panels>
  )
}

export default CompanyUsers
